import { ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { onError } from 'src/utils/CommonFunctions';

import { TabButtonProps } from './TableTabButton.interface';

const TableTabButton = ({
    tabArray,
    isTooltip,
    isActive,
    handleOnClick,
    className,
    activeClassName,
    parentClassName,
    tabTextClass,
    isTabBadgeStylePill,
    disabledClassName = '',
    tabParentClassName,
    isLoading,
    isTabDataInClick = false,
    isShowAction = false,
    viewActions = [],
    handleOutSideClick = undefined,
    isSkeletonLoading = false,
    skeletonCount = 4,
    isCollapseViews = true,
    collapseContainerSelector = 'table-header-bottom-left',
}: TabButtonProps) => {
    // Bumped whenever a view action fires. It feeds both action-menu tooltip keys so the
    // tooltip remounts and its INTERNAL open flag resets — TooltipCmp owns that flag, and
    // clearing `isShowAction` alone only blanks `message` while leaving the flag stuck true
    // (which would then need an extra click to reopen the menu).
    const [actionMenuKey, setActionMenuKey] = useState(0);
    // Identifies the menus rendered by THIS instance. Tippy appends poppers to <body>, so a
    // DOM lookup is the only way to tell our own menu from another tab bar's on the same page.
    const instanceIdRef = useRef(`tab-menu-${Math.random().toString(36).slice(2, 9)}`);

    const triggerRef = useRef<HTMLDivElement>(null);
    const tabNameRefs = useRef<Record<number, React.RefObject<HTMLSpanElement>>>({});
    const [isViewsOpen, setIsViewsOpen] = useState(false);
    const viewsTriggerRef = useRef<HTMLDivElement>(null);
    // Remount key for the collapsed "Views" menu — the only way to clear TooltipCmp's internal
    // open flag from outside, since it exposes no controlled `open` prop.
    const [viewsMenuKey, setViewsMenuKey] = useState(0);

    // Suppress the outside-click callback when the event is actually on the tab button
    // itself — otherwise clicking the active tab would close its own action dropdown
    // immediately.
    const handleConditionalOutsideClick = (event?: any) => {
        if (triggerRef.current && triggerRef.current.contains(event?.target as Node)) {
            return;
        }

        if (handleOutSideClick && typeof handleOutSideClick === 'function') {
            handleOutSideClick();
        }
    };

    // react-tippy renders a tooltip's `message` into its popper through its OWN
    // ReactDOM.render root (react-tippy.js -> updateForReact) and only unmounts that root when
    // `unmountHTMLWhenHide` is set, which TooltipCmp never sets. So every remount of a menu
    // below — a changed key — destroys the popper and strands whatever was mounted inside it.
    // A stranded TooltipCmp keeps re-rendering (it subscribes to redux) against a popper tippy
    // has already dropped, and then throws "Cannot read properties of undefined (reading
    // 'style')" out of _updateReactDom, which React escalates into a full page crash. So unmount
    // those roots ourselves: capture them now, drop them on the next tick, once the remount has
    // taken the poppers down.
    const releaseMenuContent = (kind?: 'views' | 'actions') => {
        const selector = `[data-tab-menu-id="${instanceIdRef.current}"]${kind ? `[data-tab-menu-kind="${kind}"]` : ''}`;
        const roots = Array.from(document.querySelectorAll(selector))
            .map((menu) => menu.closest('.tippy-popper')?.querySelector('.tippy-tooltip-content'))
            .filter((node): node is Element => !!node);

        if (!roots.length) {
            return;
        }

        // Legacy API on purpose: react-tippy created these roots with ReactDOM.render, so
        // ReactDOM.unmountComponentAtNode is the only thing that can take them down — there is no
        // createRoot handle to call .unmount() on.
        // eslint-disable-next-line react/no-deprecated
        setTimeout(() => roots.forEach((root) => ReactDOM.unmountComponentAtNode(root as Element)), 0);
    };

    const handleActionClick = (action: any) => (e: any) => {
        e.stopPropagation();
        action?.onClick();
        // Close the menu after the action: reset the tooltip and let the parent drop
        // `isShowAction`.
        releaseMenuContent();
        setActionMenuKey((old) => old + 1);
        setIsViewsOpen(false);

        if (handleOutSideClick && typeof handleOutSideClick === 'function') {
            handleOutSideClick();
        }
    };

    // Single renderer for the view actions (Rename/Delete), used by both the inline
    // active-tab dropdown ('list' = icon + label) and the collapsed Views dropdown row
    // ('compact' = icon only).
    const renderViewAction = (action: any, idx: number, variant: 'list' | 'compact') => {
        const IconComponent = action.icon;
        const iconClass = `w-4 h-4 flex-none ${action.isDestructive ? 'text-fgErrorPrimary' : 'text-gray500'}`;

        if (variant === 'compact') {
            return (
                <TooltipCmp key={idx} message={action.label} childrenClassName="!flex">
                    <ButtonCmp type="button" size="none" className="btn-link-secondary" onClick={handleActionClick(action)}>
                        <IconComponent className={iconClass} />
                    </ButtonCmp>
                </TooltipCmp>
            );
        }

        return (
            <li key={idx} className={`p-2 rounded-md bg-white hover:bg-gray50 flex items-center gap-x-2 cursor-pointer ${idx > 0 ? 'group' : ''}`} onClick={handleActionClick(action)}>
                <IconComponent className={iconClass} />
                <p className={`text-xs leading-[1.5] font-medium ${action.isDestructive ? 'text-fgErrorPrimary' : 'text-textSecondary group-hover:text-grayLight900'}`}>{action.label}</p>
            </li>
        );
    };

    // isTabDataInClick handlers are curried: handleOnClick(tab) returns the handler. Resolve
    // it on click, not during render. Event is forwarded so plain handlers still read
    // data-value.
    const handleTabClick = (tab: any) => (e: any) => {
        if (isTabDataInClick) {
            handleOnClick?.(tab)?.(e);

            return;
        }

        handleOnClick?.(e);
    };

    // Dynamic collapse: show all views inline by default; fold them into the "Views" dropdown
    // only when the row can't fit. We measure the REAL inline row (rowRef.scrollWidth) — no
    // duplicate markup — and remember that full width so the decision stays stable once
    // collapsed (no oscillation).
    const outerRef = useRef<HTMLDivElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);
    const fullWidthRef = useRef(0);
    const [isOverflowing, setIsOverflowing] = useState(false);
    // False until a container resolves, so the default is inert on pages that never opted in.
    const [canCollapse, setCanCollapse] = useState(false);
    // Stage 2: how many TRAILING fixed tabs also fold into the menu, once folding the saved
    // views alone still left the row too wide. 0 = every fixed tab stays inline (wal's
    // behaviour, and the only outcome whenever the row has room).
    const [hiddenFixedCount, setHiddenFixedCount] = useState(0);
    // Pill widths captured while a tab is inline, keyed by tab value — a tab that moves into
    // the menu stops being measurable, so its last known width is what stage 2 reasons about.
    const tabWidthsRef = useRef<Record<string, number>>({});
    const triggerWidthRef = useRef(0);
    // Mirror of the current render so the resize handler can read what is actually on screen
    // without being re-subscribed on every state change.
    const renderStateRef = useRef<{ inline: any[]; collapsed: boolean }>({
        inline: [],
        collapsed: false,
    });

    useEffect(() => {
        if (!isCollapseViews) {
            return;
        }

        // The row shrink-wraps, so measuring ourselves always reads as overflowing — climb to
        // the nearest named container instead. No match means never collapse.
        const getContainer = (): HTMLElement | null => (outerRef.current?.closest(`.${collapseContainerSelector}`) as HTMLElement | null) ?? null;
        // The "+" button shares the container: 26px + 4px gap, rounded up.
        const RESERVE = 35;

        // gap-1 between pills.
        const GAP = 4;

        // Record what is on screen right now. Only tabs currently inline can be measured, so
        // each value keeps its last known width while it sits in the menu.
        const captureWidths = () => {
            const row = rowRef.current;
            const { inline, collapsed } = renderStateRef.current;

            if (!row) {
                return;
            }

            inline.forEach((tab: any, i: number) => {
                const width = (row.children[i] as HTMLElement | undefined)?.offsetWidth ?? 0;

                if (width > 0) {
                    tabWidthsRef.current[String(tab.value)] = width;
                }
            });

            // The trigger is rendered straight after the inline pills.
            if (collapsed) {
                const width = (row.children[inline.length] as HTMLElement | undefined)?.offsetWidth ?? 0;

                if (width > 0) {
                    triggerWidthRef.current = width;
                }
            }
        };

        const measure = () => {
            const resolved = getContainer();
            setCanCollapse(!!resolved);

            const available = resolved?.clientWidth ?? 0;

            if (available <= 0) {
                return;
            }

            captureWidths();

            setIsOverflowing((prev) => {
                // Only trust the live row width while it's inline (holds the full set); once
                // collapsed reuse the remembered full width so removing the views can't flip us
                // back.
                if (!prev) {
                    const full = rowRef.current?.scrollWidth ?? 0;

                    if (full > 0) {
                        fullWidthRef.current = full;
                    }
                }

                const overflowing = fullWidthRef.current > 0 ? fullWidthRef.current + RESERVE > available : prev;

                // Stage 2 — walk the fixed tabs left to right and keep the ones that still fit
                // beside the trigger. Purely arithmetic over remembered widths, so it lands on the
                // same answer every pass instead of hiding one more tab per frame.
                setHiddenFixedCount(() => {
                    const fixed = (tabArray || []).filter((tab: any) => !tab?.isIconShow);

                    if (!overflowing || fixed.length === 0) {
                        return 0;
                    }

                    let used = RESERVE + (triggerWidthRef.current || 0);
                    let fits = 0;

                    for (const tab of fixed) {
                        const width = tabWidthsRef.current[String(tab.value)] ?? 0;

                        // A width we have never seen means the row has not been laid out yet — hide
                        // nothing rather than guess.
                        if (width <= 0) {
                            return 0;
                        }

                        if (used + width + GAP > available) {
                            break;
                        }

                        used += width + GAP;
                        fits += 1;
                    }

                    return fixed.length - fits;
                });

                return overflowing;
            });
        };

        const raf = requestAnimationFrame(measure);
        // Feature-detected, same as TruncateText/SelectBox: Safari < 13.1 lacks
        // ResizeObserver and jsdom doesn't implement it at all. The window listener below
        // still covers viewport changes when it's missing.
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        const container = getContainer();

        if (observer && container) {
            observer.observe(container);
        }
        window.addEventListener('resize', measure);

        return () => {
            cancelAnimationFrame(raf);
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [isCollapseViews, collapseContainerSelector, tabArray]);

    // Collapse (opt-in + actually overflowing): fixed tabs stay inline; saved views
    // (isIconShow) move into the "Views" dropdown. When it fits, everything renders inline
    // exactly as before.
    const collapseNow = isCollapseViews && isOverflowing;
    // Only a bar that actually HAS saved views can collapse, and only such a bar takes
    // the scroll/min-w-0 treatment below. wal applies those unconditionally, but pages
    // here deliberately wrap their pills instead of scrolling them behind a hidden
    // scrollbar (AllBrokers), and that fix must survive.
    const hasCollapsibleViews = isCollapseViews && (tabArray || []).some((tab: any) => tab?.isIconShow);
    const viewTabs = collapseNow ? (tabArray || []).filter((tab: any) => tab?.isIconShow) : [];
    const fixedTabs = collapseNow ? (tabArray || []).filter((tab: any) => !tab?.isIconShow) : tabArray;
    // Stage 2 only ever runs once the menu already exists for the saved views, so a bar with
    // no views keeps every fixed tab inline exactly as before. Always leave one tab on the
    // bar — a row that is nothing but a dropdown reads as a broken toolbar, not a tab strip.
    const hiddenFixedTabs = collapseNow && viewTabs.length > 0 ? fixedTabs.slice(fixedTabs.length - Math.min(hiddenFixedCount, Math.max(0, fixedTabs.length - 1))) : [];
    const inlineTabs = hiddenFixedTabs.length ? fixedTabs.slice(0, fixedTabs.length - hiddenFixedTabs.length) : fixedTabs;
    // Menu contents in visual order: overflowed status tabs first, then the saved views.
    const collapsedTabs = [...hiddenFixedTabs, ...viewTabs];
    const activeViewTab = collapsedTabs.find((tab: any) => tab.value === isActive);

    renderStateRef.current = { inline: inlineTabs || [], collapsed: collapseNow };

    // Click-away for the action menu. Done with a document listener rather than TooltipCmp's
    // isTooltipInteractive, because that renders a full-screen overlay that swallows hover on
    // the rest of the tab row. Clicks on the pill are skipped (it toggles the menu itself) and
    // so are clicks inside THIS instance's tippy popper, so the Rename/Delete items still fire.
    useEffect(() => {
        if (!isShowAction) {
            return;
        }

        const handleDocumentClick = (event: MouseEvent) => {
            // Scoped to our own popper: a page can hold several tab bars, and a click inside
            // ANOTHER instance's menu is an outside click for us — it must close this menu
            // instead of leaving two menus open at once.
            const popper = (event.target as HTMLElement)?.closest?.('.tippy-popper');

            if (popper?.querySelector(`[data-tab-menu-id="${instanceIdRef.current}"]`)) {
                return;
            }

            handleConditionalOutsideClick(event);
        };

        // Capture phase: the Views trigger and TooltipCmp's interactive overlay both call
        // stopPropagation, which would keep a bubbling listener on another instance from ever
        // running.
        document.addEventListener('click', handleDocumentClick, true);

        return () => document.removeEventListener('click', handleDocumentClick, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowAction, handleOutSideClick]);

    // Click-away for the collapsed "Views" menu, mirroring the inline action menu above so
    // both behave the same: the rest of the page stays hoverable and a click outside lands on
    // its target directly (no overlay to dismiss first) while still closing this menu.
    useEffect(() => {
        if (!isViewsOpen) {
            return;
        }

        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // The trigger toggles itself — remounting here would swap the node out from under its
            // own click handler and the menu would never reopen.
            if (viewsTriggerRef.current?.contains(target)) {
                return;
            }

            // Our own menu: view rows and the compact action icons close it through their
            // handlers.
            if (target?.closest?.('.tippy-popper')?.querySelector(`[data-tab-menu-id="${instanceIdRef.current}"]`)) {
                return;
            }

            releaseMenuContent('views');
            setViewsMenuKey((old) => old + 1);
            setIsViewsOpen(false);
        };

        document.addEventListener('click', handleDocumentClick, true);

        return () => document.removeEventListener('click', handleDocumentClick, true);
    }, [isViewsOpen]);

    // Single source of truth for rendering a tab/view. 'inline' = the pill in the bar;
    // 'dropdown' = the compact row inside the collapsed "Views" menu. Same data, design
    // switches by variant.
    const renderTab = (tab: any, index: number, variant: 'inline' | 'dropdown' = 'inline') => {
        if (variant === 'dropdown') {
            const isActiveView = isActive === tab.value;

            return (
                <li
                    key={tab.value}
                    data-value={tab.value}
                    className={`p-2 rounded-md flex items-center justify-between gap-x-3 cursor-pointer hover:bg-gray50 ${isActiveView ? 'bg-gray50' : 'bg-white'}`}
                    onClick={(e) => {
                        if (!isActiveView) {
                            releaseMenuContent('views');
                            handleTabClick(tab)(e);
                        }
                        setIsViewsOpen(false);
                    }}
                >
                    <span data-value={tab.value} className="text-xs leading-[1.5] font-medium text-left truncate">
                        {tab.name}
                    </span>
                    {/* Rename/Edit/Delete belong to saved views only — a status tab that overflowed
              into this menu has nothing to act on. */}
                    {isActiveView && tab?.isIconShow && viewActions.length > 0 && (
                        <div className="flex items-center gap-x-2 flex-none">{viewActions.map((action, idx) => renderViewAction(action, idx, 'compact'))}</div>
                    )}
                </li>
            );
        }

        // The pill itself. It is wrapped in a TooltipCmp ONLY when this bar has view
        // actions — the tooltip exists to host that menu, and its wrapper element would
        // otherwise sit between the pill and the flex row, which breaks pages that wrap
        // their pills (AllBrokers passes parentClassName="flex-wrap").
        const pill = (
            <div
                className={`${className} relative px-4 py-[5px] text-textSecondary text-xs leading-[1.5] font-semibold rounded-lg  ${isLoading ? 'custom-loading' : ''} ${
                    isActive === tab.value ? activeClassName ?? 'bg-utilityGray200' : 'bg-white'
                } ${tab.disabled ? `${disabledClassName || ''}` : 'cursor-pointer hover:bg-utilityGray100'}`}
                data-value={tab.value}
                data-disabled={tab.disabled ? 'true' : 'false'}
                onClick={handleTabClick(tab)}
                data-test-id={tab.name}
                ref={isShowAction && isActive === tab.value && viewActions.length > 0 ? triggerRef : null}
            >
                {tab.image && <img data-value={tab.value} className="rounded border border-utilityGray200 w-5 h-5 flex-none" onError={onError} src={tab.image} />}

                {tab.icon && (
                    <span data-value={tab.value} className="pointer-events-none">
                        {tab.icon}
                    </span>
                )}
                <div className="flex items-center gap-x-1.5 min-w-0">
                    {(() => {
                        if (!tabNameRefs.current[index]) {
                            tabNameRefs.current[index] = React.createRef<HTMLSpanElement>();
                        }

                        return (
                            <TruncateTextCmp message={tab.name} refName={tabNameRefs.current[index]}>
                                <span data-value={tab.value} ref={tabNameRefs.current[index]} className={`${tabTextClass} truncate max-w-[150px] block items-center gap-x-1`}>
                                    {tab.name}
                                    {tab.customValue !== null && tab.customValue !== undefined && tab.customValue}
                                </span>
                            </TruncateTextCmp>
                        );
                    })()}
                    {tab?.count && tab?.count > 0 && tab?.value !== 'All' ? (
                        <BadgeCmp style={`${isTabBadgeStylePill ? 'pill' : 'modern'}`} type="gray" isHidePillDot dataValue={tab.value} mainClassName="bg-white !py-px !px-[5px]">
                            {tab?.count}
                        </BadgeCmp>
                    ) : (
                        ''
                    )}
                    {isActive === tab.value && tab?.isIconShow && <ChevronDown className="w-4 h-4 flex-none" />}
                </div>
            </div>
        );

        if (viewActions.length === 0) {
            return <React.Fragment key={index}>{pill}</React.Fragment>;
        }

        return isTooltip ? (
            <TooltipCmp
                key={index}
                message={tab.tooltipValue}
                childrenClassName={`${className} text-xs font-semibold text-center ${
                    isActive === tab.value ? activeClassName ?? 'bg-white shadow-sm text-textSecondary' : 'bg-white text-textSecondary '
                } ${tab.disabled ? `${disabledClassName || ''}` : 'cursor-pointer hover:bg-utilityGray100'}`}
            >
                <div data-value={tab.value} data-disabled={tab.disabled ? 'true' : 'false'} onClick={handleTabClick(tab)} data-test-id={tab.name} className={`flex items-center justify-center `}>
                    {tab.image && <img data-value={tab.value} className="rounded border border-utilityGray200 w-5 h-5 flex-none" onError={onError} src={tab.image} />}

                    {tab.icon && (
                        <span data-value={tab.value} className="pointer-events-none">
                            {tab.icon}
                        </span>
                    )}
                    <span data-value={tab.value} className={`${tabTextClass}`}>
                        {tab.name}
                    </span>
                </div>
            </TooltipCmp>
        ) : (
            <React.Fragment key={index}>
                <TooltipCmp
                    // openOnClick toggles TooltipCmp's internal open flag on EVERY click of the pill —
                    // including the click that only selects the tab, when `message` is still null and
                    // nothing shows. That leaves the flag stuck true, so the next click (the one meant
                    // to open the menu) toggles it back to false and the menu needs a third click.
                    // Keying on active-ness remounts the tooltip when the tab is selected, clearing
                    // the stale flag.
                    key={`${index}-${isActive === tab.value}-${actionMenuKey}`}
                    TooltipCustomClass="[&>*]:!shadow-none"
                    parentClassName="!p-0 !shadow-none !border-0 !max-w-[220px]"
                    isArrow={false}
                    position="bottom"
                    openOnClick
                    onOpenChange={(open) => !open && handleConditionalOutsideClick()}
                    message={
                        isShowAction && isActive === tab.value && viewActions.length > 0 ? (
                            <ul data-tab-menu-id={instanceIdRef.current} data-tab-menu-kind="actions" className="rounded-[10px] border border-utilityGray200 bg-white p-1.5 w-[151px] shadow-lg">
                                {viewActions.map((action, idx) => renderViewAction(action, idx, 'list'))}
                            </ul>
                        ) : null
                    }
                >
                    {pill}
                </TooltipCmp>
            </React.Fragment>
        );
    };

    return (
        <div ref={outerRef} className={`flex items-center gap-x-2 ${canCollapse && hasCollapsibleViews ? 'min-w-0 overflow-x-auto scrollbar-hide' : ''} ${tabParentClassName}`}>
            <div ref={rowRef} className={`flex w-fit custom-scrollbar gap-1 relative ${parentClassName} `}>
                {inlineTabs.map((tab: any, index: number) => renderTab(tab, index))}
                {collapseNow && viewTabs.length > 0 && (
                    // Views menu is a TooltipCmp (openOnClick) instead of a locally positioned
                    // dropdown: tippy appends it to <body>, so the menu is never clipped by the tab
                    // row and can't turn the row into a vertical scroller. The key remounts — and
                    // therefore closes — the menu once a different view is picked, an action fires, or
                    // a click lands outside. No isTooltipInteractive here: its full-screen overlay
                    // would block hover on the rest of the page and spend the first click dismissing
                    // itself. Click-away is the document listener above instead, matching the inline
                    // action menu.
                    <div ref={viewsTriggerRef} className="flex-none" onClick={(e) => e.stopPropagation()}>
                        <TooltipCmp
                            key={`${isActive}-${actionMenuKey}-${viewsMenuKey}`}
                            TooltipCustomClass="[&>*]:!shadow-none"
                            parentClassName="!p-0 !shadow-none !border-0 !max-w-[220px]"
                            isArrow={false}
                            position="bottom"
                            openOnClick
                            onOpenChange={setIsViewsOpen}
                            message={
                                <ul
                                    data-tab-menu-id={instanceIdRef.current}
                                    data-tab-menu-kind="views"
                                    className="rounded-[10px] border border-utilityGray200 bg-white p-1.5 min-w-[120px] max-w-[400px] shadow-lg"
                                >
                                    {/* One flat list: overflowed status tabs first, then the saved views. No
                      headings or divider — it reads as a single menu, not two stacked ones. */}
                                    {collapsedTabs.map((tab: any, index: number) => renderTab(tab, index, 'dropdown'))}
                                </ul>
                            }
                        >
                            <div
                                className={`${className} px-4 py-[5px] text-textSecondary text-xs leading-[1.5] font-semibold rounded-lg cursor-pointer flex items-center gap-x-1.5 ${
                                    isLoading ? 'custom-loading' : ''
                                } ${activeViewTab || isViewsOpen ? activeClassName ?? 'bg-utilityGray200' : 'bg-white hover:bg-utilityGray100'}`}
                            >
                                <span className={`${tabTextClass} truncate max-w-[150px]`}>{activeViewTab ? activeViewTab.name : 'Views'}</span>
                                <BadgeCmp style={`${isTabBadgeStylePill ? 'pill' : 'modern'}`} type="gray" isHidePillDot mainClassName="bg-white !py-px !px-[5px]">
                                    {collapsedTabs.length}
                                </BadgeCmp>
                                <ChevronDown className="w-4 h-4 flex-none" />
                            </div>
                        </TooltipCmp>
                    </div>
                )}
                {isSkeletonLoading && (
                    <>
                        {[...Array(skeletonCount)].map((_, index) => (
                            <div key={`skeleton-${index}`} className="h-7 w-20 bg-utilityGray200 rounded-lg animate-pulse" />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

export default TableTabButton;
